import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useLogout } from '../../hooks/useAuth';

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Overview of all SEO tasks and progress' },
  '/sitewide-tasks': { title: 'Sitewide Fixes', subtitle: 'Technical SEO tasks across the entire site' },
  '/page-tasks': { title: 'Page Tasks', subtitle: 'SEO optimization tasks by page type' },
  '/weekly-plan': { title: 'Weekly Plan', subtitle: 'Kanban board for weekly task management' },
  '/export': { title: 'CSV Export', subtitle: 'Export tasks and view export history' },
  '/settings': { title: 'Settings', subtitle: 'Manage team, preferences, and data' },
};

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const logout = useLogout();
  const pageInfo = pageTitles[location.pathname] || { title: 'Dashboard', subtitle: '' };

  const isSitewideTasks = location.pathname === '/sitewide-tasks';
  const isPageTasks = location.pathname === '/page-tasks';
  const isExport = location.pathname === '/export';

  return (
    <header
      className="bg-white flex items-center justify-between px-6 flex-shrink-0"
      style={{
        height: 64,
        borderBottom: '1px solid #E4E4E7',
      }}
    >
      {/* Left: page title */}
      <div>
        <h1 className="text-lg font-bold text-[#18181B] leading-tight">{pageInfo.title}</h1>
        {pageInfo.subtitle && (
          <p className="text-xs text-[#71717A] mt-0">{pageInfo.subtitle}</p>
        )}
      </div>

      {/* Right: actions + user */}
      <div className="flex items-center gap-3">
        {/* PKT Clock */}
        <div className="text-xs text-[#A1A1AA] font-mono hidden md:block">
          <PKTClock />
        </div>

        {/* Context-sensitive primary action */}
        {(isSitewideTasks || isPageTasks) && (
          <button
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3.5 py-2 rounded-lg transition-all duration-150"
            style={{ backgroundColor: '#F97316' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EA6C0A')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#F97316')}
            onClick={() => {
              // Dispatch a custom event that the page component can listen to
              window.dispatchEvent(new CustomEvent('header:add-task'));
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Task
          </button>
        )}

        {/* Export CSV button (shown on export page or dashboard) */}
        {isExport && (
          <button
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#18181B] px-3.5 py-2 rounded-lg border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] transition-all duration-150"
            onClick={() => window.dispatchEvent(new CustomEvent('header:export-csv'))}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
        )}

        {/* Divider */}
        <div className="w-px h-6 bg-[#E4E4E7]" />

        {/* User avatar */}
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: '#F97316' }}
          >
            {((user as any)?.display_name || user?.username)?.charAt(0).toUpperCase() ?? 'U'}
          </div>
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-sm font-medium text-[#18181B]">
              {(user as any)?.display_name || user?.username}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#F97316' }}>
              {(user as any)?.role === 'member' ? 'Member' : 'Admin'}
            </span>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={() => logout.mutate()}
          className="p-2 text-[#A1A1AA] hover:text-[#DC2626] hover:bg-red-50 rounded-lg transition-all duration-150"
          title="Sign out"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
    </header>
  );
}

function PKTClock() {
  const [time, setTime] = React.useState('');

  React.useEffect(() => {
    const update = () => {
      const now = new Date();
      const pkt = new Date(now.getTime() + 5 * 60 * 60 * 1000);
      setTime(pkt.toISOString().replace('T', ' ').substring(0, 16) + ' PKT');
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);

  return <span>{time}</span>;
}
